import React, { useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Chip, TextField, Typography } from '@mui/material';
import {
  OPERATOR_LABELS,
  canAdd,
  checkTokens,
  describeFormula,
  evaluateTokens,
  hasVariableDivisor,
  normalizeNumber,
  usedIds
} from './formulaModel';
import type { Addable, ColumnIndex, FormulaToken, Operator } from './formulaModel';
import { GROUPS } from './revenueCalc';
import { formatNumber } from './revenueOps';
import type { RevenueColumn } from './revenueApi';

const BORDER = '1px solid #e2e8f0';
const DEFAULT_SAMPLE = '100';
const OPERATORS: Operator[] = ['+', '-', '*', '/'];

const KEY_BUTTON_SX = {
  minWidth: 44,
  px: 1,
  py: 0.5,
  fontSize: '1rem',
  fontWeight: 700,
  textTransform: 'none',
  borderRadius: '8px',
  color: '#334155',
  borderColor: '#cbd5e1'
} as const;

const TEXT_BUTTON_SX = { textTransform: 'none', fontWeight: 600, color: '#475569', borderRadius: '8px' } as const;

function groupRank(column: RevenueColumn): number {
  const rank = GROUPS.indexOf(column.sortByGroup);
  return rank < 0 ? GROUPS.length : rank;
}

/** Поля в порядке групп отчета (Касса, Банк, Статистика), внутри группы - по названию */
function sortedSources(sources: RevenueColumn[]): RevenueColumn[] {
  return sources.slice().sort((a, b) => {
    const byGroup = groupRank(a) - groupRank(b);
    return byGroup !== 0 ? byGroup : a.displayName.trim().localeCompare(b.displayName.trim(), 'ru');
  });
}

function chipSx(token: FormulaToken) {
  if (token.kind === 'ref') return { backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 600 };
  if (token.kind === 'num') return { backgroundColor: '#f1f5f9', color: '#334155', border: BORDER, fontWeight: 600 };
  return { backgroundColor: '#ffffff', color: '#0f172a', border: '1px solid #cbd5e1', fontWeight: 700, minWidth: 34 };
}

function chipLabel(token: FormulaToken, index: ColumnIndex): string {
  if (token.kind === 'ref') return index[token.id] ? index[token.id].displayName.trim() : `поле ${token.id}`;
  if (token.kind === 'num') return token.text.replace('.', ',');
  if (token.kind === 'op') return OPERATOR_LABELS[token.value];
  return token.value;
}

interface FormulaEditorProps {
  tokens: FormulaToken[];
  onChange: (tokens: FormulaToken[]) => void;
  /** Колонки, которые можно подставить (числовые, без замыкания формул в круг) */
  sources: RevenueColumn[];
  index: ColumnIndex;
  /** Колонка целочисленная: результат формулы округляется, как в отчетах */
  integer: boolean;
  /** Ошибка от формы или сервера: показывается вместо подсказки */
  error?: string;
}

const FormulaEditor: React.FC<FormulaEditorProps> = ({ tokens, onChange, sources, index, integer, error }) => {
  const [numberText, setNumberText] = useState('');
  const [numberError, setNumberError] = useState('');
  const [trialOpen, setTrialOpen] = useState(false);
  const [sample, setSample] = useState<Record<number, string>>({});

  const options = useMemo(() => sortedSources(sources), [sources]);
  const check = useMemo(() => checkTokens(tokens), [tokens]);
  const ids = useMemo(() => usedIds(tokens), [tokens]);

  const append = (token: FormulaToken) => onChange(tokens.concat(token));
  const addToken = (what: Addable): boolean => canAdd(tokens, what);

  const handleNumber = () => {
    const value = normalizeNumber(numberText);
    if (value === null) {
      setNumberError('Введите число, например 1000 или 0,5');
      return;
    }
    append({ kind: 'num', text: value });
    setNumberText('');
    setNumberError('');
  };

  const removeAt = (position: number) => onChange(tokens.filter((_, i) => i !== position));

  const trial = useMemo(() => {
    if (!trialOpen || check.empty || check.problem) return null;
    const values: Record<number, number> = {};
    ids.forEach((id) => {
      const text = sample[id] === undefined ? DEFAULT_SAMPLE : sample[id];
      const parsed = Number(text.replace(',', '.'));
      values[id] = text.trim() !== '' && isFinite(parsed) ? parsed : 0;
    });
    return evaluateTokens(tokens, values, integer);
  }, [trialOpen, check, ids, sample, tokens, integer]);

  const sentence = check.empty ? '' : describeFormula(tokens, index);
  const message = error || check.problem;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box
        sx={{
          minHeight: 56,
          p: 1,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.75,
          border: message ? '1px solid #dc2626' : BORDER,
          borderRadius: '8px',
          backgroundColor: '#f8fafc'
        }}
      >
        {tokens.length === 0 ? (
          <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem', px: 0.5 }}>
            Выберите поле или введите число, затем добавьте знак действия
          </Typography>
        ) : (
          tokens.map((token, position) => (
            <Chip
              key={position}
              size="small"
              label={chipLabel(token, index)}
              onDelete={() => removeAt(position)}
              sx={{ borderRadius: '8px', ...chipSx(token) }}
            />
          ))
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Autocomplete<RevenueColumn, false, true>
          size="small"
          sx={{ flex: '1 1 260px', minWidth: 220 }}
          options={options}
          value={null as unknown as RevenueColumn}
          groupBy={(item) => item.sortByGroup || 'Без группы'}
          getOptionLabel={(item) => (item ? item.displayName.trim() : '')}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          disableClearable
          blurOnSelect
          disabled={!addToken('operand')}
          noOptionsText="Подходящих полей нет"
          onChange={(_, item) => {
            if (item) append({ kind: 'ref', id: item.id });
          }}
          renderOption={(props, item) => (
            <li {...props} key={item.id}>
              {item.displayName.trim()}
              <Box component="span" sx={{ ml: 1, color: '#94a3b8', fontSize: '0.78rem' }}>
                {item.cName.trim()}
              </Box>
            </li>
          )}
          renderInput={(params) => <TextField {...params} label="Добавить поле" placeholder="Найти по названию" />}
        />

        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            label="Число"
            value={numberText}
            disabled={!addToken('operand')}
            error={!!numberError}
            helperText={numberError || undefined}
            onChange={(event) => {
              setNumberText(event.target.value);
              setNumberError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                // Enter в поле числа добавляет число, а не сохраняет всю форму
                event.preventDefault();
                event.stopPropagation();
                if (numberText.trim() !== '') handleNumber();
              }
            }}
            sx={{ width: 120 }}
          />
          <Button
            variant="outlined"
            onClick={handleNumber}
            disabled={!addToken('operand') || numberText.trim() === ''}
            sx={{ ...TEXT_BUTTON_SX, height: 40, borderColor: '#cbd5e1' }}
          >
            Добавить
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
        {OPERATORS.map((op) => (
          <Button
            key={op}
            variant="outlined"
            disabled={!addToken(op)}
            onClick={() => append({ kind: 'op', value: op })}
            aria-label={`Знак ${OPERATOR_LABELS[op]}`}
            sx={KEY_BUTTON_SX}
          >
            {OPERATOR_LABELS[op]}
          </Button>
        ))}
        <Button
          variant="outlined"
          disabled={!addToken('open')}
          onClick={() => append({ kind: 'paren', value: '(' })}
          aria-label="Открывающая скобка"
          sx={KEY_BUTTON_SX}
        >
          (
        </Button>
        <Button
          variant="outlined"
          disabled={!addToken('close')}
          onClick={() => append({ kind: 'paren', value: ')' })}
          aria-label="Закрывающая скобка"
          sx={KEY_BUTTON_SX}
        >
          )
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button disabled={tokens.length === 0} onClick={() => onChange(tokens.slice(0, -1))} sx={TEXT_BUTTON_SX}>
          Удалить последнее
        </Button>
        <Button disabled={tokens.length === 0} onClick={() => onChange([])} sx={TEXT_BUTTON_SX}>
          Очистить
        </Button>
      </Box>

      {message ? (
        <Typography sx={{ color: '#dc2626', fontSize: '0.8rem' }}>{message}</Typography>
      ) : sentence ? (
        <Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>
          Значение = <b style={{ color: '#334155', fontWeight: 600 }}>{sentence}</b>
        </Typography>
      ) : null}

      {!message && hasVariableDivisor(tokens) && (
        <Typography sx={{ color: '#b45309', fontSize: '0.8rem' }}>
          Если делитель окажется нулем, значение колонки будет 0
        </Typography>
      )}

      {!check.empty && !check.problem && (
        <Box>
          <Button size="small" onClick={() => setTrialOpen((prev) => !prev)} sx={{ ...TEXT_BUTTON_SX, px: 0 }}>
            {trialOpen ? 'Скрыть пробный расчет' : 'Пробный расчет'}
          </Button>
          {trialOpen && (
            <Box sx={{ mt: 0.5, p: 1.5, border: BORDER, borderRadius: '8px', backgroundColor: '#ffffff' }}>
              {ids.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1.25, mb: 1.25 }}>
                  {ids.map((id) => (
                    <TextField
                      key={id}
                      size="small"
                      label={index[id] ? index[id].displayName.trim() : `Поле ${id}`}
                      value={sample[id] === undefined ? DEFAULT_SAMPLE : sample[id]}
                      onChange={(event) => setSample((prev) => ({ ...prev, [id]: event.target.value }))}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          event.stopPropagation();
                        }
                      }}
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  ))}
                </Box>
              )}
              <Typography sx={{ color: '#475569', fontSize: '0.88rem' }}>
                Результат:{' '}
                <b style={{ color: '#0f172a' }}>{trial ? formatNumber(trial.value) : '-'}</b>
                {trial && trial.zeroDivision && (
                  <Box component="span" sx={{ ml: 1, color: '#b45309', fontSize: '0.8rem' }}>
                    деление на ноль, в отчетах будет 0
                  </Box>
                )}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FormulaEditor;
